import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { PortalShell } from "@/components/layout/PortalShell";
import { ADMIN_NAV } from "@/lib/admin-nav";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { SecondaryButton } from "@/components/brand/SecondaryButton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  adminListProducts,
  adminListOrders,
  adminGetOrderDetail,
  adminRefundOrder,
  adminListLibraryGrants,
  adminGrantLibrary,
  adminRevokeLibrary,
} from "@/lib/store-admin.functions";
import { formatPrice } from "@/lib/store";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/store")({
  component: AdminStorePage,
});

function Pill({
  tone = "neutral",
  children,
}: {
  tone?: "neutral" | "navy" | "bronze" | "success" | "warning" | "danger";
  children: React.ReactNode;
}) {
  const map = {
    neutral: "bg-charcoal-500/10 text-charcoal-900",
    navy: "bg-navy-900/10 text-navy-900",
    bronze: "bg-bronze-600/15 text-bronze-600",
    success: "bg-[#0E7C5A]/15 text-[#0E7C5A]",
    warning: "bg-[#B7791F]/15 text-[#B7791F]",
    danger: "bg-[#B43A3A]/15 text-[#B43A3A]",
  } as const;
  return (
    <span
      className={`inline-flex items-center rounded-sm px-2 py-0.5 font-sans text-[10px] font-semibold uppercase tracking-[0.18em] ${map[tone]}`}
    >
      {children}
    </span>
  );
}

function AdminStorePage() {
  return (
    <PortalShell nav={ADMIN_NAV} title="Currency Thoughts Store">
      <div className="flex items-end justify-between">
        <div>
          <Eyebrow>Currency Thoughts Store</Eyebrow>
          <h1 className="mt-2 font-serif text-3xl text-navy-900">
            Curated resources, stewarded with care.
          </h1>
        </div>
      </div>

      <Tabs defaultValue="products" className="mt-10">
        <div className="relative">
          <TabsList className="bg-transparent border-b border-bronze-600/30 rounded-none w-full justify-start p-0 h-auto flex-nowrap overflow-x-auto md:overflow-visible scroll-smooth [scroll-snap-type:x_mandatory] [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {[
              ["products", "Products"],
              ["orders", "Orders"],
              ["library", "Library Access"],
            ].map(([v, l]) => (
              <TabsTrigger
                key={v}
                value={v}
                className="shrink-0 [scroll-snap-align:start] rounded-none border-b-2 border-transparent data-[state=active]:border-bronze-600 data-[state=active]:bg-transparent data-[state=active]:text-navy-900 px-5 py-3 font-sans text-[11px] uppercase tracking-[0.22em] text-charcoal-500"
              >
                {l}
              </TabsTrigger>
            ))}
          </TabsList>
          <span
            aria-hidden
            className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-cream-50 to-transparent md:hidden"
          />
        </div>
        <TabsContent value="products" className="mt-8">
          <ProductsTab />
        </TabsContent>
        <TabsContent value="orders" className="mt-8">
          <OrdersTab />
        </TabsContent>
        <TabsContent value="library" className="mt-8">
          <LibraryAccessTab />
        </TabsContent>
      </Tabs>
    </PortalShell>
  );
}

// ============ Products Tab ============

function ProductsTab() {
  const fetchList = useServerFn(adminListProducts);
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "store", "products"],
    queryFn: () => fetchList(),
  });
  const navigate = useNavigate();
  const [status, setStatus] = useState<"all" | "published" | "draft">("all");

  const rows = (data ?? []).filter((p) => {
    if (status === "published" && !p.published) return false;
    if (status === "draft" && p.published) return false;
    return true;
  });

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as never)}
            className="rounded-sm border border-bronze-600/30 bg-white px-3 py-2 font-sans text-sm"
          >
            <option value="all">All status</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
        </div>
        <PrimaryButton onClick={() => navigate({ to: "/admin/store/products/$id", params: { id: "new" } })}>
          + New Product
        </PrimaryButton>
      </div>

      {isLoading ? (
        <p className="mt-8 font-sans text-charcoal-500">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="mt-8 rounded-sm border border-bronze-600/30 bg-white p-12 text-center font-sans text-charcoal-500">
          No products yet. Create your first resource to begin building the Store.
        </p>
      ) : (
        <div className="mt-6 overflow-hidden rounded-sm border border-bronze-600/20 bg-white">
          <table className="w-full border-collapse text-left font-sans text-sm">
            <thead>
              <tr className="border-b border-bronze-600/30 bg-cream-50/60 text-[10px] uppercase tracking-[0.22em] text-charcoal-500">
                <th className="px-4 py-3">Title</th>
                <th>Type</th>
                <th>Category</th>
                <th>Price</th>
                <th>Sales</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="border-b border-bronze-600/10 hover:bg-cream-50/40">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {p.thumbnail_url ? (
                        <img src={p.thumbnail_url} alt="" className="h-10 w-10 rounded-sm object-cover" />
                      ) : (
                        <div className="h-10 w-10 rounded-sm bg-bronze-600/10" />
                      )}
                      <div>
                        <p className="font-serif text-navy-900">{p.title}</p>
                      </div>
                    </div>
                  </td>
                  <td><Pill tone="navy">{p.product_type}</Pill></td>
                  <td><Pill>{p.category}</Pill></td>
                  <td className="font-mono">{formatPrice(p.price_cents)}</td>
                  <td>{p.sales_count}</td>
                  <td>
                    {p.published ? <Pill tone="success">Published</Pill> : <Pill tone="warning">Draft</Pill>}
                  </td>
                  <td className="px-4 text-right">
                    <Link
                      to="/admin/store/products/$id"
                      params={{ id: p.id }}
                      className="font-sans text-xs uppercase tracking-[0.22em] text-bronze-600 underline"
                    >
                      Edit
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============ Orders Tab ============

function OrdersTab() {
  const fetchOrders = useServerFn(adminListOrders);
  const fetchDetail = useServerFn(adminGetOrderDetail);
  const refund = useServerFn(adminRefundOrder);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "store", "orders"],
    queryFn: () => fetchOrders(),
  });
  const [openId, setOpenId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<string>("all");

  const detail = useQuery({
    queryKey: ["admin", "store", "order", openId],
    queryFn: () => fetchDetail({ data: { orderId: openId! } }),
    enabled: !!openId,
  });

  const rows = (data ?? []).filter((o) => {
    if (status !== "all" && o.status !== status) return false;
    if (search && !o.buyer_label.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  async function onRefund() {
    const reason = prompt("Reason for refund?");
    if (!reason || !openId) return;
    try {
      await refund({ data: { orderId: openId, reason } });
      toast.success("Order refunded.");
      qc.invalidateQueries({ queryKey: ["admin", "store", "orders"] });
      setOpenId(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed");
    }
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Search by buyer…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="rounded-sm border border-bronze-600/30 bg-white px-3 py-2 font-sans text-sm"
        >
          <option value="all">All status</option>
          <option value="pending">Pending</option>
          <option value="paid">Paid</option>
          <option value="refunded">Refunded</option>
        </select>
      </div>

      {isLoading ? (
        <p className="mt-8 font-sans text-charcoal-500">Loading…</p>
      ) : (
        <div className="mt-6 overflow-hidden rounded-sm border border-bronze-600/20 bg-white">
          <table className="w-full border-collapse text-left font-sans text-sm">
            <thead>
              <tr className="border-b border-bronze-600/30 bg-cream-50/60 text-[10px] uppercase tracking-[0.22em] text-charcoal-500">
                <th className="px-4 py-3">Order</th>
                <th>Buyer</th>
                <th>Items</th>
                <th>Total</th>
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((o) => (
                <tr
                  key={o.id}
                  onClick={() => setOpenId(o.id)}
                  className="cursor-pointer border-b border-bronze-600/10 hover:bg-cream-50/40"
                >
                  <td className="px-4 py-3 font-mono text-xs">{o.id.slice(0, 8)}</td>
                  <td>
                    <span>{o.buyer_label}</span>
                    {o.is_guest && <span className="ml-2"><Pill>Guest</Pill></span>}
                  </td>
                  <td>{o.item_count}</td>
                  <td className="font-mono">{formatPrice(o.total_cents)}</td>
                  <td>
                    <Pill
                      tone={
                        o.status === "paid"
                          ? "success"
                          : o.status === "refunded"
                            ? "danger"
                            : "warning"
                      }
                    >
                      {o.status}
                    </Pill>
                  </td>
                  <td className="text-charcoal-500">
                    {new Date(o.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!openId} onOpenChange={(o) => !o && setOpenId(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-navy-900">Order detail</DialogTitle>
          </DialogHeader>
          {detail.data?.order ? (
            <div className="space-y-4 font-sans text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.22em] text-charcoal-500">Order</p>
                  <p className="font-mono text-xs">{detail.data.order.id}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.22em] text-charcoal-500">Buyer</p>
                  <p>{detail.data.order.guest_email ?? detail.data.order.user_id ?? "—"}</p>
                </div>
              </div>
              <ul className="divide-y divide-bronze-600/20 rounded-sm border border-bronze-600/20">
                {detail.data.items.map((it) => (
                  <li key={it.id} className="flex justify-between p-3">
                    <span>{it.product_title_snapshot}</span>
                    <span className="font-mono">{formatPrice(it.price_cents_snapshot)}</span>
                  </li>
                ))}
              </ul>
              {detail.data.agreement && (
                <p className="text-charcoal-500">
                  PMA signed by {detail.data.agreement.signed_name} on{" "}
                  {new Date(detail.data.agreement.signed_at).toLocaleString()}
                </p>
              )}
            </div>
          ) : (
            <p className="font-sans text-charcoal-500">Loading…</p>
          )}
          <DialogFooter>
            {detail.data?.order?.status === "paid" && (
              <SecondaryButton onClick={onRefund}>Issue refund</SecondaryButton>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============ Library Access Tab ============

function LibraryAccessTab() {
  const fetchGrants = useServerFn(adminListLibraryGrants);
  const grant = useServerFn(adminGrantLibrary);
  const revoke = useServerFn(adminRevokeLibrary);
  const fetchProducts = useServerFn(adminListProducts);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "store", "library"],
    queryFn: () => fetchGrants(),
  });
  const products = useQuery({
    queryKey: ["admin", "store", "products"],
    queryFn: () => fetchProducts(),
  });
  const [email, setEmail] = useState("");
  const [productId, setProductId] = useState("");
  const [reason, setReason] = useState("");

  const grantMut = useMutation({
    mutationFn: () => grant({ data: { userEmail: email, productId, reason } }),
    onSuccess: () => {
      toast.success("Access granted.");
      setEmail("");
      setProductId("");
      setReason("");
      qc.invalidateQueries({ queryKey: ["admin", "store", "library"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed"),
  });

  async function onRevoke(id: string) {
    const r = prompt("Reason for revoking?");
    if (!r) return;
    try {
      await revoke({ data: { libraryId: id, reason: r } });
      toast.success("Revoked.");
      qc.invalidateQueries({ queryKey: ["admin", "store", "library"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed");
    }
  }

  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_320px]">
      <div>
        {isLoading ? (
          <p className="font-sans text-charcoal-500">Loading…</p>
        ) : (
          <div className="overflow-hidden rounded-sm border border-bronze-600/20 bg-white">
            <table className="w-full border-collapse text-left font-sans text-sm">
              <thead>
                <tr className="border-b border-bronze-600/30 bg-cream-50/60 text-[10px] uppercase tracking-[0.22em] text-charcoal-500">
                  <th className="px-4 py-3">User</th>
                  <th>Product</th>
                  <th>Granted</th>
                  <th>Downloads</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(data ?? []).map((r) => {
                  const p = r.product as unknown as { title: string } | null;
                  return (
                    <tr key={r.id} className="border-b border-bronze-600/10">
                      <td className="px-4 py-3">{r.user_email}</td>
                      <td>{p?.title ?? "—"}</td>
                      <td className="text-charcoal-500">{new Date(r.granted_at).toLocaleDateString()}</td>
                      <td>{r.download_count}</td>
                      <td className="text-right pr-4">
                        <button
                          onClick={() => onRevoke(r.id)}
                          className="font-sans text-xs uppercase tracking-[0.22em] text-[#B43A3A] underline"
                        >
                          Revoke
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <aside className="rounded-sm border border-bronze-600/30 bg-white p-6">
        <Eyebrow>Manual grant</Eyebrow>
        <div className="mt-4 space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-[0.22em] text-charcoal-500">User email</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.22em] text-charcoal-500">Product</Label>
            <select
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              className="mt-1 w-full rounded-sm border border-bronze-600/30 bg-white px-3 py-2 font-sans text-sm"
            >
              <option value="">Select…</option>
              {(products.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.22em] text-charcoal-500">Reason</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="mt-1"
              rows={3}
            />
          </div>
          <PrimaryButton
            onClick={() => grantMut.mutate()}
            disabled={!email || !productId || reason.length < 2 || grantMut.isPending}
          >
            Grant access
          </PrimaryButton>
        </div>
      </aside>
    </div>
  );
}
